@let _isReadonly = editorIsReadonly | async;

<ada-editor-toolbar
	[editorState]="state"
	[isAnywhereEditor]="isAnywhereEditor"
	[isSaveButtonEnabled]="saveButtonEnabled | async"
>
	<ada-push-button
		data-container-selector="editor-toolbar-buttons"
		data-testid="save-and-deploy-button"
		look="Outline"
		text="Save & Deploy"
		[adaClick]="saveAndDeploy | adaBindMethod: this"
		[colSpan]="0"
		[isDisabled]="(saveButtonEnabled | async) !== true"
	/>
	<ada-container
		#DeploymentStatusWrapper
		data-container-selector="editor-toolbar-content"
		id="DeploymentStatusWrapper"
		padding="{{ ui.Spacing.SPACING_MEDIUM }}"
		[colCount]="0"
		[horizontalSpacing]="'Small'"
		[verticalAlignmentOfChildren]="'Middle'"
	>
		<ada-text
			#DeploymentStatusLabel
			id="DeploymentStatusLabel"
			[content]="'Deployment Status:'"
			[textColor]="'primary'"
		/>
		<ada-text
			#DeploymentStatusDisplay
			id="DeploymentStatusDisplay"
			[colSpan]="0"
			[content]="deployStatusTextChange | async"
		/>

		@if (deployButtonEnabled | async) {
			<ada-push-button
				#DeployButton
				data-testid="deploy-button"
				id="DeployButton"
				look="Outline"
				size="Small"
				text="Deploy"
				[adaClick]="deploy | adaBindMethod: this"
			/>
		}
	</ada-container>
</ada-editor-toolbar>
<ada-container
	class="ada-background-color-background-2"
	id="BodyContainer"
	[horizontalSpacing]="'Medium'"
	[padding]="defaultBodyPadding"
	[verticalSpacing]="'Medium'"
>
	<ada-card
		id="GeneralSettingsCard"
		[colCount]="0"
		[collapseEnabled]="true"
	>
		<ada-text
			data-container-selector="ada-card-header"
			id="GeneralSettingsHeaderText"
			[content]="'General Settings'"
		/>
		<ada-container
			data-container-selector="ada-card-body"
			id="GeneralSettingsBodyContainer"
			[colCount]="4"
			[horizontalSpacing]="'Medium'"
			[padding]="defaultCardPadding"
			[verticalSpacing]="'Medium'"
		>
			<ada-label
				content="Name"
				id="NameInputLabel"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
			/>
			<ada-input
				#NameInput
				data-testid="name-input"
				id="NameInput"
				[debounce]="'1000'"
				[isDisabled]="_isReadonly"
				[placeholder]="'Schedule Name'"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
				[showAsFullWidth]="true"
				[value]="state?.value?.name"
				(valueChange)="setName($event)"
			/>
			<ada-label
				content="Description"
				id="DescriptionInputLabel"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
			/>
			<ada-input
				data-testid="description-input"
				id="DescriptionInput"
				[isDisabled]="_isReadonly"
				[minHeight]="'120px'"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
				[showAsFullWidth]="true"
				[type]="'Textarea'"
				[value]="state?.value?.description"
				(valueChange)="setDescription($event)"
			/>
		</ada-container>
	</ada-card>
	<ada-card
		id="ScheduleSettingsCard"
		[collapseEnabled]="true"
	>
		<ada-container
			data-container-selector="ada-card-header"
			id="ScheduleSettingsHeaderContainer"
			[colCount]="0"
			[horizontalSpacing]="'Small'"
			[verticalAlignmentOfChildren]="'Middle'"
		>
			<ada-text
				id="ScheduleSettingsHeaderText"
				[content]="'Schedule Settings'"
			/>
			<ada-signpost
				id="ScheduleSettingsSignpost"
				[icon]="'faInfoCircleOutline'"
			>
				<ada-text
					id="ScheduleSettingsSignpostText"
					[content]="
						'The Start time specifies when the schedule will begin firing. If the Start time has already passed then it will fire immediately, subject to service window constraints.'
					"
				/>
			</ada-signpost>
		</ada-container>
		<ada-container
			data-container-selector="ada-card-body"
			id="ScheduleSettingsBodyContainer"
			[colCount]="4"
			[horizontalSpacing]="'Medium'"
			[padding]="defaultCardPadding"
			[verticalSpacing]="'Medium'"
		>
			<ada-label
				id="UserServerTimeZoneLabel"
				[content]="'Use Server Timezone'"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
			/>
			<ada-toggle-switch
				#UseServerTimeZoneToggle
				data-testid="use-server-timezone-toggle"
				id="UseServerTimeZoneToggle"
				[isDisabled]="_isReadonly || state?.value?.scheduleType === 1"
				[isToggled]="state?.value?.useServerTimeZone"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
				(isToggledChange)="setUseServerTimeZone($event)"
			/>
			<ada-label
				id="StartTimeLabel"
				[content]="'Start Time'"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
			/>
			<ada-input
				#StartTimeInput
				data-testid="start-time-input"
				id="StartTimeInput"
				[isDisabled]="_isReadonly || state?.value?.scheduleType === 1"
				[outputDateAsInteger]="true"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
				[showAsFullWidth]="true"
				[type]="'Date-time'"
				(valueChange)="setStartTime($event)"
			/>
			<ada-label
				id="EnableEndTimeLabel"
				[content]="'Enable End Time'"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
			/>
			<ada-toggle-switch
				#EnableEndTimeToggle
				data-testid="enable-end-time-toggle"
				id="EnableEndTimeToggle"
				[isDisabled]="true"
				[isToggled]="!!(state?.value && state?.value?.endTime)"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
				(isToggledChange)="enableEndTimeChanged($event)"
			/>
			<ada-label
				id="EndTimeLabel"
				[content]="'End Time'"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
			/>
			<ada-input
				#EndTimeInput
				data-testid="end-time-input"
				id="EndTimeInput"
				[isDisabled]="_isReadonly || !state?.value || !state?.value?.endTime"
				[outputDateAsInteger]="true"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
				[showAsFullWidth]="true"
				[type]="'Date-time'"
				(userInput)="setEndTime($event)"
			/>
			<ada-label
				content="Execution Offset"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
			>
				<ada-text
					content="The amount of time to wait after the Schedule's start time before execution. Only applies to recurring schedule types."
					data-container-selector="label-info-text"
				/>
			</ada-label>
			<ada-container
				verticalSpacing="small"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
			>
				@let _isOffsetDisabled = (editorIsReadonly | async) || state?.value?.scheduleType <= 2;

				<ada-time-scale
					data-testid="ada-schedule-editor-execution-offset-scale"
					[isDisabled]="_isOffsetDisabled"
					[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
					[scaleConfiguration]="offsetTimeScale"
					[value]="state?.value?.executionOffset | adaMathAbs"
					(valueChange)="setScheduleOffset($event)"
				/>
				<ada-radio-button
					[buttons]="offsetTypeOptions"
					[isDisabled]="_isOffsetDisabled || !state?.value?.executionOffset"
					[value]="state?.value?.executionOffset | adaMathSign"
					(valueChange)="toggleOffsetType($event)"
				/>
			</ada-container>
			<ada-label
				id="ScheduleRepeatLabel"
				[content]="'Schedule Repeat'"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
			/>
			<ada-radio-button
				#ScheduleTypeRadioButton
				data-testid="schedule-type-radio-button"
				id="ScheduleTypeRadioButton"
				[buttons]="scheduleTypeOptions"
				[isDisabled]="_isReadonly"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
				[value]="state?.value?.scheduleType"
				(valueChange)="setScheduleType($event)"
			/>

			@switch (state?.value?.scheduleType) {
				@case (scheduleTypes.RECURRING_BY_INTERVAL) {
					<app-recurring-by-interval
						#ScheduleTypeComponent
						data-testid="recurring-by-interval-schedule-component"
						[isDisabled]="_isReadonly"
						[schedule]="state?.value"
						(valueChange)="updateFromScheduleType($event)"
					/>
				}

				@case (scheduleTypes.RECURRING_BY_DAY) {
					<app-recurring-by-day
						#ScheduleTypeComponent
						data-testid="recurring-by-day-schedule-component"
						[isDisabled]="_isReadonly"
						[schedule]="state?.value"
						(valueChange)="updateFromScheduleType($event)"
					/>
				}

				@case (scheduleTypes.RECURRING_BY_WEEK) {
					<app-recurring-by-week
						#ScheduleTypeComponent
						data-testid="recurring-by-week-schedule-component"
						[isDisabled]="_isReadonly"
						[schedule]="state?.value"
						(valueChange)="updateFromScheduleType($event)"
					/>
				}

				@case (scheduleTypes.RECURRING_BY_DATE) {
					<app-recurring-by-date
						#ScheduleTypeComponent
						data-testid="recurring-by-date-schedule-component"
						[isDisabled]="_isReadonly"
						[schedule]="state?.value"
						(valueChange)="updateFromScheduleType($event)"
					/>
				}

				@case (scheduleTypes.RECURRING_BY_LAST_DAY) {
					<app-recurring-by-last-day
						#ScheduleTypeComponent
						data-testid="recurring-by-last-day-schedule-component"
						[isDisabled]="_isReadonly"
						[schedule]="state?.value"
						(valueChange)="updateFromScheduleType($event)"
					/>
				}

				@case (scheduleTypes.RECURRING_BY_DAY_OF_WEEK) {
					<app-recurring-by-day-of-week
						#ScheduleTypeComponent
						data-testid="recurring-by-day-of-week-schedule-component"
						[isDisabled]="_isReadonly"
						[schedule]="state?.value"
						(valueChange)="updateFromScheduleType($event)"
					/>
				}
			}
		</ada-container>
	</ada-card>
	<ada-card
		id="ConstraintsCard"
		[collapseEnabled]="true"
	>
		<ada-container
			data-container-selector="ada-card-header"
			id="ConstraintsHeaderContainer"
			[colCount]="0"
			[horizontalSpacing]="'Small'"
			[verticalAlignmentOfChildren]="'Middle'"
		>
			<ada-text
				id="ConstraintsHeaderText"
				[content]="'Additional Time Constraints'"
			/>
			<ada-signpost
				id="ConstraintsSignpost"
				[icon]="'faInfoCircleOutline'"
			>
				<ada-text
					id="ConstraintsSignpostText"
					[content]="'If a Service Window Constraint is specified, the Schedule will only run within the provided range.'"
				/>
			</ada-signpost>
		</ada-container>
		<ada-container
			data-container-selector="ada-card-body"
			id="ConstraintsBodyContainer"
			[colCount]="4"
			[horizontalSpacing]="'Medium'"
			[padding]="defaultCardPadding"
			[verticalSpacing]="'Medium'"
		>
			<ada-label
				id="AdditionalConstraintsLabel"
				[content]="'Additional Time Constraints'"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
			/>
			<ada-toggle-switch
				id="AdditionaTimeConstraintsToggle"
				[isDisabled]="_isReadonly"
				[isToggled]="additionalConstraints"
				[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
				(isToggledChange)="setUseAdditionalTimeConstraints($event)"
			/>
			@if (state?.value?.serviceWindow) {
				<ada-label
					id="ServiceWindowUseServerTimezoneLabel"
					[content]="'Use Server Timezone'"
					[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
				/>
				<ada-toggle-switch
					data-testid="service-window-use-server-timezone-toggle"
					id="ServiceWindowUseServerTimezoneToggle"
					[colSpan]="3"
					[isDisabled]="_isReadonly"
					[isToggled]="state?.value && state?.value?.serviceWindow && state?.value?.serviceWindow.useServerTimeZone"
					[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
					(isToggledChange)="setServiceWindowUseServerTimeZone($event)"
				/>
				<ada-label
					id="TimeSlotsLabel"
					[content]="'Time Slots'"
					[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
				/>
				<ada-container
					id="AddTimeSlotsButtonContainer"
					[colSpan]="3"
				>
					<ada-push-button
						data-testid="add-time-slots-button"
						id="AddTimeSlotsButton"
						[adaClick]="openTimeSlotOverlay | adaBindMethod: this"
						[isDisabled]="_isReadonly"
						[look]="'Outline'"
						[size]="'Small'"
						[text]="'Add Time Slots'"
					/>
				</ada-container>
				<ada-container
					id="TimeSlotsTableContainer"
					[colCount]="2"
					[colOffset]="1"
					[colSpan]="3"
				>
					<ada-table
						data-testid="time-slots-table"
						id="TimeSlotsTable"
						[colSpan]="2"
						[data]="timeSlots$ | async"
						[includeActionsColumn]="true"
						[includeSorting]="false"
						[rowMenu]="TimeSlotsTableMenu"
					>
						<ng-template
							adaTableColulmn
							let-row
						>
							<ada-text
								id="Text4"
								[content]="row.startTime"
							/>
						</ng-template>
						<ng-template
							adaTableColulmn
							let-row
						>
							<ada-text
								id="Text5"
								[content]="row.endTime"
							/>
						</ng-template>
						<ng-template
							adaTableColulmn
							let-row
						>
							<ada-text
								id="Text6"
								[content]="row.days"
							/>
						</ng-template>
					</ada-table>
					<ada-menu
						#TimeSlotsTableMenu
						data-testid="time-slots-table-menu-item"
						[items]="timeSlotsTableMenuItems"
					/>
				</ada-container>
				<ada-label
					id="LoadLevelingInputLabel"
					[content]="'Load Leveling Duration'"
					[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
				/>
				<ada-time-scale
					#LoadLevelingInput
					data-testid="load-leveling-time-scale-input"
					id="LoadLevelingInput"
					[isDisabled]="_isReadonly"
					[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
					[scaleConfiguration]="slotTimeScaleConfig"
					[value]="state?.value?.serviceWindow?.loadLevelingDurationInMilliseconds"
					(valueChange)="setLoadLevelingDuration($event)"
				/>
				<ada-label
					id="OverrideDurationInputLabel"
					[content]="'Override Duration'"
					[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
				/>
				<ada-time-scale
					#OverrideDurationInput
					data-testid="override-duration-time-scale-input"
					[isDisabled]="_isReadonly"
					[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
					[scaleConfiguration]="slotTimeScaleConfig"
					[value]="state?.value?.serviceWindow?.overrideDurationInMilliseconds"
					(valueChange)="setOverrideDuration($event)"
				/>
			}
		</ada-container>
	</ada-card>
</ada-container>

<ada-overlay
	#TimeSlotOverlay
	id="TimeSlotOverlay"
	[beforeSubmit]="validateTimeSlot | adaBindMethod: this"
	[title]="'New Time Slot'"
	(submit)="addTimeSlot($event)"
>
	<ada-container
		*adaTemplateMarker
		id="TimeSlotOverlayContainer"
		[colCount]="4"
		[horizontalSpacing]="'Medium'"
		[padding]="defaultBodyPadding"
		[verticalSpacing]="'Medium'"
	>
		@if (timeSlotError$ | async; as _error) {
			<ada-alert
				id="TimeSlotErrorAlert"
				[colSpan]="4"
				[isClosable]="false"
				[title]="_error"
				[type]="'Error'"
			/>
		}
		<ada-label
			id="TimeSlotStartTimeInputLabel"
			[content]="'Start Time'"
			[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
		/>
		<ada-input
			#StartTimeInput
			data-testid="timeslot-overlay-start-time-input"
			id="TimeSlotStartTimeInput"
			[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
			[showAsFullWidth]="true"
			[type]="'Time'"
			[(value)]="timeSlotStartTime"
		/>
		<ada-label
			id="TimeSlotEndTimeLabel"
			[content]="'End Time'"
			[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
		/>
		<ada-input
			data-testid="timeslot-overlay-end-time-input"
			id="TimeSlotEndTimeInput"
			[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
			[showAsFullWidth]="true"
			[type]="'Time'"
			[(value)]="timeSlotEndTime"
		/>
		<ada-label
			id="TimeSlotWeekdaysLabel"
			[content]="'Days of the Week'"
			[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_LABEL_CONFIG"
		/>
		<ada-checkbox-group
			data-testid="timeslot-weekday-checkbox"
			id="TimeSlotWeekdayCheckboxGroup"
			[options]="dayOptions"
			[responsiveConfiguration]="ui.ResponsiveConfig.DEFAULT_FIELD_CONFIG"
			[(ngModel)]="timeSlotWeekdaySelections"
		/>
	</ada-container>
</ada-overlay>
